import {getIntersectCordsBetweenLines, getMidPoint, getPointDistance} from "./sqftHelper";

/**
 * Get point position left or right from the line
 *  if D = 0 - then searchedPoint is on the line
 *  if D < 0 - then searchedPoint is on the left from the line
 *  if D > 0 - then searchedPoint is on the right from the line
 * @param linePoint1 {{x: int, y: int}}
 * @param linePoint2 {{x: int, y: int}}
 * @param searchedPoint {{x: int, y: int}}
 */
export const getThePositionOfAPointToTheLeftOrRightOfALine = (linePoint1, linePoint2, searchedPoint) => {
  return (searchedPoint.x - linePoint1.x) * (linePoint2.y - linePoint1.y) - (searchedPoint.y - linePoint1.y) * (linePoint2.x - linePoint1.x)
}

function crossProduct(ax, ay, bx, by) {
  return ax * by - ay * bx;
}

function direction(ax, ay, bx, by, cx, cy) {
  return crossProduct(cx - ax, cy - ay, bx - ax, by - ay);
}

function onSegment(ax, ay, bx, by, cx, cy) {
  const withinX = (ax <= cx && cx <= bx) || (ax >= cx && cx >= bx);
  const withinY = (ay <= cy && cy <= by) || (ay >= cy && cy >= by);
  return withinX && withinY;
}

/**
 * Tests if line a-b intersects with line c-d, allow overlap will return false if lines are over each other
 * @param ax
 * @param ay
 * @param bx
 * @param by
 * @param cx
 * @param cy
 * @param dx
 * @param dy
 * @param allowOverlap
 */
export const getIntersectionCordsV2 = (ax, ay, bx, by, cx, cy, dx, dy, allowOverlap = true) => {
  const d1 = direction(ax, ay, bx, by, cx, cy);
  const d2 = direction(ax, ay, bx, by, dx, dy);
  const d3 = direction(cx, cy, dx, dy, ax, ay);
  const d4 = direction(cx, cy, dx, dy, bx, by);

  if (d1 * d2 < 0 && d3 * d4 < 0) {
    const A1 = by - ay;
    const B1 = ax - bx;
    const C1 = A1 * ax + B1 * ay;
    const A2 = dy - cy;
    const B2 = cx - dx;
    const C2 = A2 * cx + B2 * cy;

    const det = A1 * B2 - A2 * B1;
    if (det === 0) { // Collinear lines
      return null;
    } else {
      const x = (B2 * C1 - B1 * C2) / det;
      const y = (A1 * C2 - A2 * C1) / det;
      return [x, y];
    }
  }

  if (!allowOverlap && d1 === 0 && d2 === 0 && (onSegment(ax, ay, bx, by, cx, cy) || onSegment(ax, ay, bx, by, dx, dy))) {
    return null;  //overlap each other
  }

  if ((d1 === 0 && onSegment(ax, ay, bx, by, cx, cy)) ||
    (d2 === 0 && onSegment(ax, ay, bx, by, dx, dy)) ||
    (d3 === 0 && onSegment(cx, cy, dx, dy, ax, ay)) ||
    (d4 === 0 && onSegment(cx, cy, dx, dy, bx, by))) {
    return null; //overlap only in one point
  }

  return null;
}

// Define a function to calculate the cross product of two vectors in 2D.
export function crossProduct2D(vectorA, vectorB) {
  // Calculate the cross product. Since the vectors are in 2D, the cross product will be a scalar.
  // vectorA.x * vectorB.y - vectorA.y * vectorB.x gives the Z component of the 3D cross product.
  return vectorA.x * vectorB.y - vectorA.y * vectorB.x;
}

/**
 * Finds the line in the `lines` array that is closest to referenceLine
 * and intersects the perpendicular line drawn from midpoint of referenceLine.
 *
 * @param {Line} referenceLine The line from which the perpendicular is drawn.
 * @param {Line[]} lines The array of lines to search in.
 * @return {{Line, point} | null} The found line or null if no line is found.
 */
export function findClosestIntersectingPointAndLine(referenceLine, lines) {
  // Find the perpendicular line to referenceLine
  const perpendicularVector = getPerpendicularMidpointVector(referenceLine);

  // Find all intersection points of the perpendicular line with the lines in the array
  const intersectionPointsAndLines = [];
  for (const line of lines) {
    // skip referenceLine
    if (line.id === referenceLine.id) {
      continue
    }
    //const intersectionPoint = getVectorIntersectionPoint(perpendicularVector, line);
    const intersectionPoint = getIntersectCordsBetweenLines(perpendicularVector.from, perpendicularVector.to, line.from, line.to, true ,true);

    if (intersectionPoint) {
      intersectionPointsAndLines.push({
        line,
        point: intersectionPoint,
      });
    }
  }

  return getClosestLineAndIntersectionPoint(referenceLine, intersectionPointsAndLines)
}

/**
 * Finds the line in the `lines` array that is closest to referenceLine
 * and intersects the perpendicular line drawn from midpoint of referenceLine.
 *
 * @return {{Line, point} | null} The found line or null if no line is found.
 * @param line
 * @param intersectionPointsAndLines
 */
export const getClosestLineAndIntersectionPoint = (line, intersectionPointsAndLines) => {
  let closestIntersectionPointAndLine = null;
  let minDistance = Infinity;
  for (const intersection of intersectionPointsAndLines) {
    const midPoint = getMidPoint(line.from, line.to);
    const distance = getPointDistance(midPoint, intersection.point);
    if (distance < minDistance) {
      minDistance = distance;
      closestIntersectionPointAndLine = intersection;
    }
  }

  return closestIntersectionPointAndLine;
}

export const getPerpendicularMidpointVector = (line) => {
  const midPoint = getMidPoint(line.from, line.to);
  const vector = {
    x: line.from.y - line.to.y, // Изменение координат Y для получения перпендикуляра
    y: line.to.x - line.from.x, // Изменение координат X для получения перпендикуляра
  };

  // Вектор, начинающийся в серединной точке и проходящий через нее
  return {
    from: midPoint,
    to: {
      x: midPoint.x + vector.x,
      y: midPoint.y + vector.y,
    },
  };
}
